/**
 * Prompt 版本管理页 (B5)
 *
 * Phase 3-2 / Plan §5.5 B5 / Design §8.2 prompt 权限点。
 *
 * P0-1 修复：字段名全改（version/active/BIGINT epoch ms），删 notes/activatedAt/activatedBy/variables。
 * P2 修复：删除 diffBaseId/diffTargetId 死状态；创建表单 version 字段 + content maxLength={60000}。
 *
 * 权限：
 *   - prompt:read  — 查看版本列表、详情、diff（super_admin + operator）
 *   - prompt:publish — 激活版本 / 创建新版本（super_admin + operator）
 *   - prompt:rollback — 回滚（super_admin + operator）
 *
 * 功能：
 *   - 顶部 task_type 选择器（Select），选择后下方显示版本列表
 *   - 版本列表（Table）：version / active / createdAt / 操作
 *   - 详情 Drawer：content 全文（<pre>）
 *   - Diff Modal：side-by-side <pre> 对比两版本 content（zero dependency）
 *   - 激活 Modal.confirm → 调 activate API（约 30s 内生效）
 *   - 回滚按钮 → 调 rollback API（回滚到 created_at DESC 最近非 active 版本）
 *   - 创建版本 Modal
 *
 * 无 prompt:read 时渲染 403 提示（roles.ts：super_admin + operator）。
 */
import { useState } from 'react';
import {
  Select,
  Table,
  Tag,
  Button,
  Space,
  Drawer,
  Modal,
  Form,
  Input,
  message,
  Result,
  Typography,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPromptVersions,
  listTaskTypes,
  getPromptVersion,
  getPromptDiff,
  createPromptVersion,
  activatePromptVersion,
  rollbackPromptVersion,
  type PromptVersionDto,
  type PromptDiffResponse,
} from '../../api/prompts';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';

const { TextArea } = Input;

// ── 辅助函数 ─────────────────────────────────────────────────────────────────

/** epoch ms → 本地时间字符串（P0-1: createdAt 为 number | null） */
function formatEpoch(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return new Date(v).toLocaleString('zh-CN');
}

// ── 主页面（权限门控）────────────────────────────────────────────────────────

export default function PromptsPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.PROMPT_READ}
      fallback={
        <Result status="403" title="403" subTitle="无权访问 Prompt 版本管理" />
      }
    >
      <PromptsPageInner />
    </PermissionGate>
  );
}

function PromptsPageInner() {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  // 权限
  const canPublish = usePermission(PERMISSIONS.PROMPT_PUBLISH);
  const canRollback = usePermission(PERMISSIONS.PROMPT_ROLLBACK);

  // task_type 选择
  const [selectedTaskType, setSelectedTaskType] = useState<string | undefined>(undefined);

  // 详情 Drawer
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Diff Modal（P2: 删除 diffBaseId/diffTargetId 死状态，改用局部变量）
  const [diffModalOpen, setDiffModalOpen] = useState(false);
  const [diffData, setDiffData] = useState<PromptDiffResponse | null>(null);

  // 创建版本 Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  // P2: 字数计数（content maxLength=60000）
  const [contentLength, setContentLength] = useState(0);

  // 分页
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // task_type 列表
  const { data: taskTypes } = useQuery({
    queryKey: ['prompt-task-types'],
    queryFn: listTaskTypes,
    staleTime: 5 * 60 * 1000,
  });

  // 版本列表（依赖 selectedTaskType）
  const { data: listData, isLoading } = useQuery({
    queryKey: ['prompt-versions', selectedTaskType, page, pageSize],
    queryFn: () =>
      listPromptVersions({
        taskType: selectedTaskType,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    enabled: true, // 即使未选 taskType 也可以列出全部
  });

  // 详情查询（Drawer 打开时）
  const { data: detailVersion } = useQuery({
    queryKey: ['prompt-version-detail', selectedVersionId],
    queryFn: () => getPromptVersion(selectedVersionId!),
    enabled: !!selectedVersionId && detailDrawerOpen,
  });

  // ── 操作函数 ─────────────────────────────────────────────────────────────

  async function handleActivate(version: PromptVersionDto) {
    Modal.confirm({
      title: '确认激活该版本？',
      content: `版本 ${version.version} 将成为当前活动版本，切换后约 30s 内生效。`,
      okText: '确认激活',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await activatePromptVersion(version.id);
          if (result.broadcastError) {
            message.warning(
              `激活写入成功，但 content-pipeline 广播失败（${result.broadcastError.message}），请重试激活或检查服务状态`,
            );
          } else {
            message.success('版本已激活，约 30s 内生效');
          }
          queryClient.invalidateQueries({ queryKey: ['prompt-versions'] });
        } catch {
          message.error('激活失败，请稍后再试');
        }
      },
    });
  }

  async function handleRollback() {
    if (!selectedTaskType) {
      message.error('请先选择任务类型');
      return;
    }
    Modal.confirm({
      title: '确认回滚？',
      content: `将回滚 ${selectedTaskType} 到上一个版本（按创建时间倒序），约 30s 内生效。`,
      okText: '确认回滚',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await rollbackPromptVersion(selectedTaskType);
          if (result.broadcastError) {
            message.warning(
              `回滚写入成功，但 content-pipeline 广播失败（${result.broadcastError.message}）`,
            );
          } else {
            message.success('已回滚到上一版本，约 30s 内生效');
          }
          queryClient.invalidateQueries({ queryKey: ['prompt-versions'] });
        } catch {
          message.error('回滚失败，请检查是否存在历史版本');
        }
      },
    });
  }

  async function handleShowDiff(version: PromptVersionDto) {
    // P2: 找其他 active 版本进行比较；若点击的本身是 active 版本，提示用户选择
    const otherItem = listData?.items.find((v) => v.id !== version.id && v.active);
    if (!otherItem) {
      // P2: 修正提示：不再说"未找到 active"，改为说明语义
      message.info('请在列表中找另一个版本作为比较目标（当前仅支持与 active 版本对比）');
      return;
    }
    try {
      // P2: 两版本必须同 task_type（admin-service 有校验）
      const data = await getPromptDiff(version.id, otherItem.id);
      setDiffData(data);
      setDiffModalOpen(true);
    } catch {
      message.error('获取版本对比失败，两版本可能属于不同任务类型');
    }
  }

  async function handleCreateVersion(values: {
    version: string;
    content: string;
  }) {
    if (!selectedTaskType) {
      message.error('请先选择任务类型');
      return;
    }
    setCreateLoading(true);
    try {
      await createPromptVersion({
        taskType: selectedTaskType,
        version: values.version,
        content: values.content,
      });
      message.success('版本创建成功');
      setCreateModalOpen(false);
      setContentLength(0);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['prompt-versions'] });
    } catch {
      message.error('创建失败，请检查版本号是否重复');
    } finally {
      setCreateLoading(false);
    }
  }

  // ── 表格列（P0-1: versionLabel → version，isActive → active，删 activatedAt/activatedBy/notes）

  const columns: ColumnsType<PromptVersionDto> = [
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 160,
    },
    {
      title: '状态',
      dataIndex: 'active',
      key: 'active',
      width: 90,
      render: (v: boolean) =>
        v ? <Tag color="green">Active</Tag> : <Tag>历史</Tag>,
    },
    {
      title: '任务类型',
      dataIndex: 'taskType',
      key: 'taskType',
      width: 160,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: formatEpoch, // P0-1: epoch ms
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 210,
      render: (_: unknown, version: PromptVersionDto) => (
        <Space>
          <Button
            size="small"
            data-testid={`prompts-version-row-${version.id}`}
            onClick={() => {
              setSelectedVersionId(version.id);
              setDetailDrawerOpen(true);
            }}
          >
            详情
          </Button>
          <Button
            size="small"
            onClick={() => handleShowDiff(version)}
          >
            比较
          </Button>
          {canPublish && !version.active && (
            <Button
              size="small"
              type="primary"
              data-testid={`prompts-activate-btn-${version.id}`}
              onClick={() => handleActivate(version)}
            >
              激活
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="prompts-page">
      {/* 顶部工具栏：task_type 选择 + 回滚 + 新建 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Select
            allowClear
            placeholder="选择任务类型"
            style={{ width: 220 }}
            value={selectedTaskType}
            onChange={(v) => {
              setSelectedTaskType(v);
              setPage(1);
            }}
            options={(taskTypes ?? []).map((t) => ({ value: t, label: t }))}
            data-testid="prompts-task-type-select"
          />
        </Col>
        {canRollback && (
          <Col>
            <Button
              danger
              data-testid="prompts-rollback-btn"
              onClick={handleRollback}
            >
              回滚到上一版
            </Button>
          </Col>
        )}
        {canPublish && (
          <Col>
            <Button
              type="primary"
              data-testid="prompts-create-btn"
              onClick={() => setCreateModalOpen(true)}
              disabled={!selectedTaskType}
            >
              新建版本
            </Button>
          </Col>
        )}
      </Row>

      {/* 版本列表 */}
      <Table<PromptVersionDto>
        rowKey="id"
        columns={columns}
        dataSource={listData?.items ?? []}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total: listData?.total ?? 0,
          onChange: (p) => setPage(p),
        }}
      />

      {/* 详情 Drawer（P0-1: 删 activatedAt/activatedBy/notes，改 version/active/createdAt epoch） */}
      <Drawer
        title="Prompt 版本详情"
        width={720}
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
      >
        {detailVersion && (
          <div>
            <p><strong>版本号：</strong>{detailVersion.version}</p>
            <p>
              <strong>状态：</strong>
              {detailVersion.active ? <Tag color="green">Active</Tag> : <Tag>历史</Tag>}
            </p>
            <p><strong>任务类型：</strong>{detailVersion.taskType}</p>
            <p><strong>创建时间：</strong>{formatEpoch(detailVersion.createdAt)}</p>
            <p><strong>更新时间：</strong>{formatEpoch(detailVersion.updatedAt)}</p>
            <p><strong>创建人：</strong>{detailVersion.createdBy ?? '-'}</p>
            <p><strong>Prompt 内容：</strong></p>
            <Typography.Text>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  overflowY: 'auto',
                  maxHeight: 400,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {detailVersion.content}
              </pre>
            </Typography.Text>
          </div>
        )}
      </Drawer>

      {/* Diff Modal（side-by-side <pre>，zero dependency；P0-1: version 字段）*/}
      <Modal
        title="版本内容对比"
        open={diffModalOpen}
        onCancel={() => setDiffModalOpen(false)}
        footer={null}
        width="80%"
        data-testid="prompts-diff-modal"
      >
        {diffData && (
          <Row gutter={16}>
            <Col span={12}>
              <p><strong>{diffData.versionA.version}</strong></p>
              <pre
                style={{
                  background: '#fafafa',
                  padding: 12,
                  borderRadius: 4,
                  overflowY: 'auto',
                  maxHeight: 500,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #e8e8e8',
                }}
              >
                {diffData.versionA.content}
              </pre>
            </Col>
            <Col span={12}>
              <p><strong>{diffData.versionB.version}</strong></p>
              <pre
                style={{
                  background: '#fafafa',
                  padding: 12,
                  borderRadius: 4,
                  overflowY: 'auto',
                  maxHeight: 500,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: '1px solid #e8e8e8',
                }}
              >
                {diffData.versionB.content}
              </pre>
            </Col>
          </Row>
        )}
      </Modal>

      {/* 创建版本 Modal（P0-1: version 字段；P2: content maxLength + 字数计数；删 notes）*/}
      <Modal
        title="新建 Prompt 版本"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setContentLength(0);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateVersion}
        >
          <Form.Item
            name="version"
            label="版本号"
            rules={[
              { required: true, message: '请输入版本号' },
              { max: 64, message: '版本号不超过 64 字符' },
            ]}
          >
            <Input placeholder="如 v2.0 / 2026-05-25 调优" />
          </Form.Item>
          <Form.Item
            name="content"
            label={`Prompt 内容（${contentLength}/60000）`}
            rules={[
              { required: true, message: '请输入 prompt 内容' },
              { max: 60000, message: 'content 不超过 60000 字符' },
            ]}
          >
            <TextArea
              rows={8}
              placeholder="完整 prompt 文本..."
              maxLength={60000}
              onChange={(e) => setContentLength(e.target.value.length)}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setCreateModalOpen(false);
                  setContentLength(0);
                  form.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
